import { translate } from "@/modules/shared/IntlProviderWrapper";
import { classNames, fromShiftTag, toISODateString, weekToDayShift } from "@/lib/util";
import Tooltip from "@/modules/shared/ui/Tooltip";

import type { PersonData } from "@/lib/shifts";
import type { IShiftPersonLineRecItem } from "@/lib/backend/manufacturing2.generated.types";

export type AbsencesProps = {
    poolLineShifts: IShiftPersonLineRecItem[];
    absentPeopleByShiftTag: Map<string, PersonData[]>;
    scheduledPersonUuidsByShiftTag: Map<string, Set<string>>;
    scheduledAbsentPeopleByShiftTag: Map<string, PersonData[]>;
    totalAbsenceCount: number;
    scheduledAbsentPeopleCount: number;
    isExpanded: boolean;
    canEdit: boolean;
    selectedPerson: PersonData | null;
    selectedShift: string;
    selectedShiftGroup: number;
    getHolidaysForDate: (date: string) => string[];
    onToggle: () => void;
    onAbsentPersonSelect: (person: PersonData, shiftTag: string) => void;
    onClearScheduledAbsentPeople: (shiftTag: string, scheduledAbsentPeople: PersonData[]) => void;
    onClearScheduledAbsentPeopleForWeek: () => void;
};

export const Absences = ({
    poolLineShifts,
    absentPeopleByShiftTag,
    scheduledPersonUuidsByShiftTag,
    scheduledAbsentPeopleByShiftTag,
    totalAbsenceCount,
    scheduledAbsentPeopleCount,
    isExpanded,
    canEdit,
    selectedPerson,
    selectedShift,
    selectedShiftGroup,
    getHolidaysForDate,
    onToggle,
    onAbsentPersonSelect,
    onClearScheduledAbsentPeople,
    onClearScheduledAbsentPeopleForWeek,
}: AbsencesProps) => {
    if (totalAbsenceCount === 0) {
        return null;
    }

    const hasScheduledAbsentPeople = scheduledAbsentPeopleCount > 0;

    return (
        <tfoot>
            <tr
                className={classNames("shift-assignment-week-pool__tr--absences", {
                    "is-expanded": isExpanded,
                })}
            >
                <td className="shift-assignment-week-pool__td--line">
                    <div className="shift-assignment-week-pool__table-cell">
                        <button
                            className="shift-assignment-week-pool__absences-toggle btn btn-flat-light"
                            type="button"
                            aria-expanded={isExpanded}
                            onClick={onToggle}
                        >
                            <span className="shift-assignment-week-pool__absences-title">
                                {translate("ShiftAssignmentWeekPool.absences", "Absences")}
                            </span>
                            <Tooltip
                                content={
                                    <div className="shift-assignment-week-pool__absences-tooltip">
                                        <div>
                                            {translate(
                                                "ShiftAssignmentWeekPool.number_of_absences_in_week",
                                                "Total number of absences in the selected week"
                                            )}
                                        </div>
                                        {hasScheduledAbsentPeople && (
                                            <div className="shift-assignment-week-pool__absences-tooltip-warning">
                                                {translate(
                                                    "ShiftAssignmentWeekPool.scheduled_absences_warning",
                                                    "Some absent people are scheduled"
                                                )}
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <span
                                    className={classNames("shift-assignment-week-pool__absences-count", {
                                        "has-scheduled-people": hasScheduledAbsentPeople,
                                    })}
                                >
                                    {totalAbsenceCount}
                                </span>
                            </Tooltip>
                            <i className="fas fa-chevron-down" />
                        </button>
                        {isExpanded && (
                            <div className="shift-assignment-week-pool__absences-controls">
                                {hasScheduledAbsentPeople && canEdit && (
                                    <Tooltip
                                        content={translate(
                                            "ShiftAssignmentWeekPool.unschedule_absent_people_from_week",
                                            "Unschedule absent people from all shifts in this week"
                                        )}
                                    >
                                        <button
                                            type="button"
                                            className="shift-assignment-week-pool__absences-clear btn btn-danger"
                                            onClick={event => {
                                                event.stopPropagation();
                                                onClearScheduledAbsentPeopleForWeek();
                                            }}
                                        >
                                            <i className="fas fa-user-minus" />
                                            <span>
                                                {translate(
                                                    "ShiftAssignmentWeekPool.unschedule_absent",
                                                    "Unschedule absent"
                                                )}{" "}
                                                ({scheduledAbsentPeopleCount})
                                            </span>
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>
                </td>
                {poolLineShifts.map(shift => {
                    const dayShift = weekToDayShift(shift.shift_number);
                    const holidays = getHolidaysForDate(toISODateString(fromShiftTag(shift.shift_tag)));
                    const absentPeople = absentPeopleByShiftTag.get(shift.shift_tag) ?? [];
                    const scheduledPersonUuids = scheduledPersonUuidsByShiftTag.get(shift.shift_tag);
                    const scheduledAbsentPeople = scheduledAbsentPeopleByShiftTag.get(shift.shift_tag) ?? [];
                    const hasAbsentPeople = absentPeople.length > 0;
                    const shiftScheduledAbsentPeopleCount = scheduledAbsentPeople.length;
                    const hasShiftScheduledAbsentPeople = shiftScheduledAbsentPeopleCount > 0;
                    const unscheduleAbsentPeopleLabel = translate(
                        "ShiftAssignmentWeekPool.unschedule_absent_people_from_shift",
                        "Unschedule absent people from this shift"
                    );

                    return (
                        <td
                            key={shift.shift_tag}
                            className={classNames(`shift-group-${dayShift}`, {
                                "is-selected": selectedShift === shift.shift_tag,
                                "is-selected-group": selectedShiftGroup === dayShift,
                                "is-holiday": holidays.length > 0,
                            })}
                        >
                            <div className="shift-assignment-week-pool__table-cell">
                                <div
                                    className={classNames("shift-assignment-week-pool__absences-summary", {
                                        "has-scheduled-people": hasShiftScheduledAbsentPeople,
                                    })}
                                >
                                    {hasAbsentPeople && (
                                        <Tooltip
                                            content={
                                                <div className="shift-assignment-week-pool__absences-tooltip">
                                                    <div>
                                                        {translate(
                                                            "ShiftAssignmentWeekPool.number_of_absences_in_shift",
                                                            "Number of absences in this shift"
                                                        )}
                                                    </div>
                                                    {hasShiftScheduledAbsentPeople && (
                                                        <div className="shift-assignment-week-pool__absences-tooltip-warning">
                                                            {translate(
                                                                "ShiftAssignmentWeekPool.scheduled_absences_warning",
                                                                "Some absent people are scheduled"
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        >
                                            <span className={classNames("shift-assignment-week-pool__absences-count")}>
                                                {absentPeople.length}
                                            </span>
                                        </Tooltip>
                                    )}
                                    {hasShiftScheduledAbsentPeople && canEdit && (
                                        <Tooltip content={unscheduleAbsentPeopleLabel}>
                                            <button
                                                type="button"
                                                className="shift-assignment-week-pool__absences-clear btn btn-danger"
                                                aria-label={unscheduleAbsentPeopleLabel}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    onClearScheduledAbsentPeople(
                                                        shift.shift_tag,
                                                        scheduledAbsentPeople
                                                    );
                                                }}
                                            >
                                                <i className="fas fa-user-minus" />
                                                <span aria-hidden="true">{shiftScheduledAbsentPeopleCount}</span>
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                                {isExpanded && hasAbsentPeople && (
                                    <ul className="shift-assignment-week-pool__absences-list">
                                        {absentPeople.map(person => {
                                            const isScheduled = scheduledPersonUuids?.has(person.uuid) ?? false;
                                            return (
                                                <li key={person.uuid}>
                                                    <Tooltip content={person.name}>
                                                        <button
                                                            type="button"
                                                            className={classNames(
                                                                "shift-assignment-week-pool__absence-person",
                                                                {
                                                                    "is-scheduled": isScheduled,
                                                                    "is-selected":
                                                                        selectedPerson !== null &&
                                                                        selectedPerson.uuid === person.uuid,
                                                                }
                                                            )}
                                                            onClick={event => {
                                                                event.stopPropagation();
                                                                onAbsentPersonSelect(person, shift.shift_tag);
                                                            }}
                                                        >
                                                            {person.short_name}
                                                        </button>
                                                    </Tooltip>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </td>
                    );
                })}
            </tr>
        </tfoot>
    );
};
